/**
 * ⭐ 2026-10-15 (사용자 요청) — 수정 화면에서 시작 날짜를 옮기면 「매주」 요일 체크도 같은 일수만큼 따라간다.
 *
 * ```
 * 10/21(화) → 10/22(수)   화      ⇒ 수
 *                         화·목   ⇒ 수·금      (서버가 「이후」·「전체」에서 만드는 새 규칙과 같다)
 * ```
 * ⚠ 이렇게 «따라 바뀐» 요일은 「반복 설정을 바꿨다」로 치지 않는다(`recurChanged` 판정에서 이 값과도 견준다) —
 *   그래야 오늘 이후 회차의 「이 일정만」 날짜 이동이 「반복 설정 변경」으로 잠기지 않는다.
 *   사용자가 요일을 직접 다시 고르면 그 뒤로는 따라가지 않는다(EventCreateModal 의 효과).
 * ⚠ 매월·매년의 「몇째 주 요일」·날짜 문구는 시작 날짜에서 바로 계산되므로 여기서 다룰 것이 없다.
 */
import { WEEKDAY_CODES, type RecurState, type WeekdayCode } from './recurrence-form';
import { localDayNumber, shiftWeekday } from './recurrence-dates';

/** 두 현지 일시(`YYYY-MM-DDTHH:mm`)의 «날짜» 차이(일). 못 읽으면 null. */
export function localDayDelta(fromLocal: string | null | undefined, toLocal: string | null | undefined): number | null {
  const a = fromLocal ? new Date(fromLocal) : null;
  const b = toLocal ? new Date(toLocal) : null;
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return localDayNumber(b) - localDayNumber(a);
}

/** 요일들을 delta 일만큼 민다(요일 순서 일→토로 정렬, 겹치면 하나). */
export function shiftWeekdays(byDay: readonly WeekdayCode[], delta: number): WeekdayCode[] {
  const moved = new Set(byDay.map((c) => shiftWeekday(WEEKDAY_CODES, c, delta)));
  return WEEKDAY_CODES.filter((c) => moved.has(c));
}

/**
 * 처음 연 반복 상태(initial)를 시작 날짜 이동에 맞춰 «따라 바꾼» 상태. 매주가 아니거나 날짜를 못 읽으면 initial 그대로.
 */
export function followStartWeekdays(initial: RecurState, originalStartLocal: string | null, startLocal: string): RecurState {
  if (initial.freq !== 'WEEKLY' || initial.byDay.length === 0) return initial;
  const delta = localDayDelta(originalStartLocal, startLocal);
  if (delta === null || delta % 7 === 0) return initial;
  return { ...initial, byDay: shiftWeekdays(initial.byDay, delta) };
}
